import * as React from 'react'
import { useDropzone } from 'react-dropzone'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Upload } from '@factorialco/f0-react/icons/app'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import { ExpenseFileViewer as ExpenseFileViewerComponent } from 'modules/expenses/components/ExpenseFileViewer'
import { UploadFile } from 'modules/expenses/components/common/DropItem'

import './index.styles.css'
interface ViewerProps {
  currentFile: UploadFile | null
  files: UploadFile[] | []
  isPending?: boolean
  onSelectFile?: (file: UploadFile) => void
  onDrop?: (files: File[]) => void
  showUploadButton?: boolean
  isFilesFieldEnabled?: boolean
}

interface FilePreviewProps {
  file: UploadFile | null
  isPending?: boolean
  onEmptyStateButtonClick: () => void
  showUploadButton?: boolean
  isFilesFieldEnabled?: boolean
}

const FilePreview = ({
  file,
  isPending,
  onEmptyStateButtonClick,
  showUploadButton = false,
  isFilesFieldEnabled = true,
}: FilePreviewProps) => {
  if (file && !isPending) {
    if (file.type === 'application/pdf' || file.type.match(/image/)) {
      return (
        <ExpenseFileViewerComponent url={file.preview} contentType={file.type} fileId={file.id} filename={file.name} />
      )
    } else {
      return <Text.Normal>{i18n.t('expenses_v2.viewer.file_without_preview')}</Text.Normal>
    }
  } else {
    return isFilesFieldEnabled && showUploadButton ? (
      <OneEmptyState
        title={i18n.t('expenses_v2.empty_state.title')}
        description={i18n.t('expenses_v2.empty_state.description')}
        emoji="📜"
        actions={[
          {
            icon: Upload,
            label: i18n.t('expenses_v2.empty_state.button'),
            onClick: onEmptyStateButtonClick,
            variant: 'outline',
          },
        ]}
      />
    ) : (
      <Text.Normal>{i18n.t('expenses_v2.viewer.no_documents')}</Text.Normal>
    )
  }
}

const ExpenseFileViewer = ({
  files = [],
  currentFile,
  isPending,
  onSelectFile,
  onDrop,
  showUploadButton,
  isFilesFieldEnabled,
}: ViewerProps) => {
  const onDropFiles = (filesOnDrop: File[]) => {
    if (onDrop) {
      onDrop(filesOnDrop)
    }
  }

  const { open } = useDropzone({
    onDrop: onDropFiles,
  })

  const totalFiles = +files.length - 1
  const currentFileIndex = currentFile
    ? files.findIndex((file) => file.name + file.size === currentFile.name + currentFile.size)
    : 0
  const showGalleryButtons = onSelectFile && !!currentFile && files.length > 1

  const onClickBack = () => {
    const previousFile = !!currentFileIndex && files[currentFileIndex - 1]

    if (previousFile && onSelectFile) {
      onSelectFile(previousFile)
    }
  }

  const onClickNext = () => {
    const nextFile = currentFileIndex >= 0 && files[currentFileIndex + 1]

    if (nextFile && onSelectFile) {
      onSelectFile(nextFile)
    }
  }

  return (
    <Box
      background="grey200"
      flexDirection="column"
      height="full"
      justifyContent="center"
      alignItems="center"
      className={'FileViewer_previewBox'}
    >
      {showGalleryButtons && (
        <>
          <Box className={'FileViewer_leftArrow'}>
            <Button
              disabled={currentFileIndex <= 0}
              onClick={onClickBack}
              type="secondary"
              iconName="arrow-left"
            />
          </Box>
          <Box className={'FileViewer_rightArrow'}>
            <Button
              disabled={currentFileIndex >= totalFiles}
              onClick={onClickNext}
              type="secondary"
              iconName="arrow-right"
            />
          </Box>
        </>
      )}
      <FilePreview
        file={currentFile}
        isPending={isPending}
        onEmptyStateButtonClick={open}
        showUploadButton={showUploadButton}
        isFilesFieldEnabled={isFilesFieldEnabled}
      />
    </Box>
  )
}

export default observer(ExpenseFileViewer)
